import { useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Btn, PageHeader } from "@/design/primitives";
import { cs } from "@/i18n/cs";

/**
 * Společná kostra čtyř schránek (rozhodnutí, úkoly, blokace, návrhy):
 * seznam vlevo, detail vpravo, toast s vrácením zpět.
 *
 * Rozdělením na čtyři obrazovky vznikla čtyři skoro stejná rozvržení. Kostra
 * je tady proto, aby se lišil obsah, ne rám — a aby se ovládání (výběr, prázdné
 * stavy, toast) chovalo všude stejně.
 */

export function useInboxToast() {
  const [toast, setToast] = useState<{ msg: string; undoId?: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = (msg: string, undoId?: string) => {
    if (timer.current) clearTimeout(timer.current);
    setToast({ msg, undoId });
    // s undo delší okno, ať je čas se rozmyslet
    timer.current = setTimeout(() => setToast(null), undoId ? 8000 : 4000);
  };
  return { toast, showToast, clearToast: () => setToast(null) };
}

export function InboxShell<T extends { id: string }>({
  title,
  kicker,
  lead,
  right,
  items,
  selectedId,
  onSelect,
  renderRow,
  renderDetail,
  emptyList,
  listFooter,
  emptyDetail,
  emptyDetailNote,
  toast,
  onUndo,
  undoPending,
  testId,
}: {
  title: string;
  kicker: string;
  lead: string;
  right?: ReactNode;
  items: T[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  renderRow: (item: T, selected: boolean) => ReactNode;
  renderDetail: (item: T) => ReactNode;
  emptyList: ReactNode;
  /** Pod seznamem — přiznaný ořez, souhrny. Bez detailu položky. */
  listFooter?: ReactNode;
  emptyDetail: string;
  emptyDetailNote: string;
  toast?: { msg: string; undoId?: string } | null;
  onUndo?: (id: string) => void;
  undoPending?: boolean;
  testId: string;
}) {
  const selected = items.find((i) => i.id === selectedId) ?? items[0] ?? null;

  return (
    <div className="af-animate-fade">
      <PageHeader title={title} kicker={kicker} lead={lead} right={right} />
      <div className="grid min-h-[560px] grid-cols-1 items-start gap-5 xl:grid-cols-[400px_1fr]">
        <div className="flex flex-col gap-3">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item.id)}
              className="cursor-pointer px-[18px] py-4 text-left transition-colors"
              style={{
                borderRadius: "var(--radius-card)",
                border: `1px solid ${
                  selected?.id === item.id
                    ? "color-mix(in srgb, var(--accent) 50%, transparent)"
                    : "var(--border-default)"
                }`,
                background: selected?.id === item.id ? "var(--surface-raised)" : "var(--surface)",
                backdropFilter: "var(--surface-blur)",
              }}
              data-testid={`${testId}-item`}
            >
              {renderRow(item, selected?.id === item.id)}
            </button>
          ))}
          {items.length === 0 ? emptyList : null}
          {listFooter}
        </div>

        <div
          className="overflow-auto p-7"
          style={{
            borderRadius: "var(--radius-panel)",
            border: "1px solid var(--border-default)",
            background: "var(--surface)",
            backdropFilter: "var(--surface-blur)",
          }}
        >
          {toast ? (
            <div
              className="mb-3 flex items-center justify-between gap-3 px-4 py-2 text-[12px]"
              style={{
                borderRadius: "var(--radius-box)",
                border: "1px solid color-mix(in srgb, var(--positive) 30%, transparent)",
                color: "var(--positive)",
              }}
              data-testid={`${testId}-toast`}
            >
              <span>✓ {toast.msg}</span>
              {toast.undoId && onUndo ? (
                <Btn
                  variant="secondary"
                  tone="amber"
                  className="whitespace-nowrap !px-3 !py-1 !text-[12px]"
                  disabled={undoPending}
                  onClick={() => onUndo(toast.undoId!)}
                  data-testid={`${testId}-undo`}
                >
                  ↩ {cs.decisions.undo}
                </Btn>
              ) : null}
            </div>
          ) : null}
          {selected ? (
            renderDetail(selected)
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div
                  className="af-display text-[24px] font-light"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {emptyDetail}
                </div>
                <div className="mt-2 text-[12px]" style={{ color: "var(--text-tertiary)" }}>
                  {emptyDetailNote}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function InboxEmptyRow({ title, note }: { title: string; note: string }) {
  return (
    <div
      className="mt-auto px-[18px] py-4 text-center text-[12px]"
      style={{
        borderRadius: "var(--radius-card)",
        border: "1px dashed color-mix(in srgb, var(--positive) 35%, transparent)",
        color: "var(--text-tertiary)",
      }}
    >
      <span style={{ color: "var(--positive)" }}>✓ </span>
      {title} — {note}
    </div>
  );
}

/** Potvrzovací okno — chrání před překliknutím u akce, která něco spustí. */
export function ConfirmModal({
  kicker,
  itemTitle,
  label,
  body,
  bodyLabel,
  danger,
  pending,
  onConfirm,
  onCancel,
  afterNote,
  testId,
}: {
  kicker: string;
  itemTitle: string;
  label: string;
  body?: string;
  bodyLabel?: string;
  danger?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  afterNote: string;
  testId: string;
}) {
  // portál do <body>: stránkový wrapper má transform animaci, byl by
  // containing blockem a overlay by se centroval podle stránky, ne viewportu
  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center overflow-y-auto p-3"
      style={{ background: "var(--scrim)" }}
      onClick={onCancel}
      data-testid={`${testId}-confirm-modal`}
    >
      <div
        className="my-auto w-[520px] max-w-[calc(100vw-24px)] p-7"
        style={{
          borderRadius: "var(--radius-panel)",
          border: `1px solid ${danger ? "var(--critical-border)" : "var(--accent-border)"}`,
          background: "var(--surface-overlay)",
          backdropFilter: "var(--surface-blur)",
          boxShadow: danger ? "var(--emphasis-critical)" : "var(--emphasis-accent)",
        }}
      >
        <div onClick={(e) => e.stopPropagation()} className="flex flex-col gap-3">
          <div
            className="af-mono text-[11px] tracking-[0.2em]"
            style={{ color: danger ? "var(--critical)" : "var(--accent)" }}
          >
            {kicker}
          </div>
          <div
            className="af-display text-[20px] font-bold leading-snug"
            style={{ color: "var(--text-primary)" }}
          >
            {itemTitle}
          </div>
          <div
            className="px-4 py-3 text-[13px] font-semibold"
            style={{
              borderLeft: `3px solid ${danger ? "var(--critical)" : "var(--positive)"}`,
              borderRadius: "0 var(--radius-control) var(--radius-control) 0",
              background: danger ? "var(--critical-subtle)" : "var(--positive-subtle)",
              color: "var(--text-primary)",
            }}
          >
            {label}
          </div>
          {body ? (
            <div>
              <div className="af-label-dim mb-1">{bodyLabel ?? cs.decisions.confirmNoteLabel}</div>
              <div
                className="max-h-[160px] overflow-auto whitespace-pre-wrap px-3 py-2 text-[12px] leading-relaxed"
                style={{
                  borderRadius: "var(--radius-box)",
                  border: "1px solid var(--border-default)",
                  color: "var(--text-primary)",
                }}
              >
                {body}
              </div>
            </div>
          ) : null}
          <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {afterNote}
          </div>
          <div className="mt-1 flex justify-end gap-2.5">
            <Btn onClick={onCancel} tone="dim" data-testid={`${testId}-confirm-cancel`}>
              {cs.common.cancel}
            </Btn>
            <Btn
              variant={danger ? "danger" : "primary"}
              disabled={pending}
              onClick={onConfirm}
              data-testid={`${testId}-confirm`}
            >
              {cs.decisions.confirmSubmit}
            </Btn>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
